import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";
import type { KurooClient, PricingRule } from "@kuroo/sdk";
import { buildServicePricingRuleInput, emptyPricingRuleForm, errorMessage, formSchedule, formatMoney, pricingRuleForm, ruleConditions, ruleSchedule, ruleValue, weekdayLabels, type PricingRuleFormState } from "./ServicePricingRulePrimitives";

export function ServicePricingRulesEditor({ client, serviceName, targetId, basePriceCents, currency, onMessage, onError }: {
  client: KurooClient;
  serviceName: string;
  targetId: string;
  basePriceCents: number;
  currency?: string;
  onMessage: (value: string) => void;
  onError: (value: string) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [form, setForm] = useState<PricingRuleFormState>(emptyPricingRuleForm);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setRuleError] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    setRuleError(undefined);
    try {
      setRules(await client.pricingRules({ targetKind: "service", targetId }));
    } catch (reason) {
      const message = errorMessage(reason);
      setRuleError(message);
      onError(message);
    } finally {
      setLoading(false);
    }
  }, [client, onError, targetId]);

  useEffect(() => { void load(); }, [load]);

  const update = <K extends keyof PricingRuleFormState>(key: K, value: PricingRuleFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const resetForm = () => setForm(emptyPricingRuleForm());

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setRuleError(undefined);
    onError("");

    const { input, error: validationError } = buildServicePricingRuleInput(form, targetId);
    if (validationError || !input) {
      setRuleError(validationError ?? "No se pudo preparar la regla de precio.");
      setBusy(false);
      return;
    }

    try {
      if (form.id) await client.updatePricingRule(form.id, input);
      else await client.createPricingRule(input);
      resetForm();
      await load();
      onMessage(form.id ? "Regla de precio actualizada" : "Regla de precio creada");
    } catch (reason) {
      const message = errorMessage(reason);
      setRuleError(message);
      onError(message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (rule: PricingRule) => {
    setBusy(true);
    setRuleError(undefined);
    onError("");
    try {
      await client.updatePricingRule(rule.id, { enabled: !rule.enabled });
      await load();
      onMessage(rule.enabled ? "Regla desactivada" : "Regla activada");
    } catch (reason) {
      const message = errorMessage(reason);
      setRuleError(message);
      onError(message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rule: PricingRule) => {
    if (!window.confirm("¿Eliminar esta regla? Las cotizaciones y reservas existentes no cambiarán.")) return;
    setBusy(true);
    setRuleError(undefined);
    onError("");
    try {
      await client.deletePricingRule(rule.id);
      if (form.id === rule.id) resetForm();
      await load();
      onMessage("Regla de precio eliminada");
    } catch (reason) {
      const message = errorMessage(reason);
      setRuleError(message);
      onError(message);
    } finally {
      setBusy(false);
    }
  };

  const activeRules = rules.filter((rule) => rule.enabled).length;
  const statusLabel = loading ? "Consultando…" : activeRules ? `${activeRules} ${activeRules === 1 ? "activa" : "activas"}` : "Desactivado";
  return <section className="service-config-card service-pricing-card" onClick={(event) => event.stopPropagation()}>
    <header className="service-config-card-header service-pricing-header">
      <div>
        <p className="eyebrow">Precio dinámico</p>
        <h4>Reglas para {serviceName}</h4>
        <p>Definí un recargo, descuento o importe fijo según el día y la franja horaria. La API elegirá una sola regla ganadora y congelará el precio en cada hold.</p>
      </div>
      <div className="service-pricing-header-actions">
        <span className={`service-config-status ${activeRules ? "enabled" : "disabled"}`}>{statusLabel}</span>
        <button type="button" className={`button compact ${open ? "secondary" : "primary"}`} onClick={() => setOpen((value) => !value)} disabled={busy} aria-expanded={open}>
          {open ? "Ocultar reglas" : rules.length ? "Editar reglas" : "Agregar regla"}
        </button>
      </div>
    </header>
    {open && <div className="service-pricing-body">
      <div className="service-pricing-explainer">
        <div><span>Precio base actual</span><strong>{formatMoney(basePriceCents, currency)}</strong></div>
        <p>Los servicios existentes mantienen su precio base. Una regla sólo modifica nuevas cotizaciones que coincidan con sus condiciones; las reservas ya creadas no cambian.</p>
      </div>
      {error && <div className="alert" role="alert">{error}</div>}
      {loading ? <div className="state state-loading" role="status"><span className="spinner" aria-hidden="true" />Cargando reglas…</div> : rules.length ? <div className="service-pricing-rule-list">
        {rules.map((rule) => <article className={`service-pricing-rule ${rule.enabled ? "active" : "inactive"}`} key={rule.id}>
          <div className="service-pricing-rule-content">
            <div className="service-pricing-rule-title"><span className={`pricing-rule-state ${rule.enabled ? "active" : "inactive"}`}>{rule.enabled ? "Activa" : "Inactiva"}</span><strong>{ruleValue(rule, currency)}</strong></div>
            <p>{rule.label || "Regla sin nombre"}</p>
            <small>{ruleSchedule(rule)}</small>
            <small>{ruleConditions(rule)} · prioridad {rule.priority}</small>
          </div>
          <div className="inline-actions service-pricing-rule-actions"><button type="button" className="text-button" disabled={busy} onClick={() => setForm(pricingRuleForm(rule))}>Editar</button><button type="button" className="text-button" disabled={busy} onClick={() => void toggle(rule)}>{rule.enabled ? "Desactivar" : "Activar"}</button><button type="button" className="text-button danger" disabled={busy} onClick={() => void remove(rule)}>Eliminar</button></div>
        </article>)}
      </div> : <div className="service-pricing-empty"><strong>Todavía no hay reglas</strong><span>Creá una para que, por ejemplo, los turnos del sábado por la mañana tengan otro precio.</span></div>}
      <form className="service-pricing-form" onSubmit={(event) => void save(event)}>
        <div className="service-pricing-form-heading"><div><p className="eyebrow">{form.id ? "Editar regla" : "Nueva regla"}</p><h4>¿Cuándo cambia el precio?</h4><p>Completá sólo las condiciones que necesitás. Si dejás días y horarios vacíos, la regla puede aplicar todo el tiempo.</p></div><span className="service-pricing-preview">{formSchedule(form, currency)}</span></div>
        <div className="service-pricing-primary-grid">
          <label>Nombre de la regla<input value={form.label} maxLength={240} onChange={(event) => update("label", event.target.value)} placeholder="Sábado por la mañana" /></label>
          <fieldset className="service-pricing-mode">
            <legend>Tipo de precio</legend>
            <label className={form.pricingMode === "percentage" ? "selected" : ""}><input type="radio" name={`pricing-mode-${targetId}`} checked={form.pricingMode === "percentage"} onChange={() => update("pricingMode", "percentage")} /><span><strong>Porcentaje</strong><small>Calcula sobre el precio base del servicio.</small></span></label>
            <label className={form.pricingMode === "fixed" ? "selected" : ""}><input type="radio" name={`pricing-mode-${targetId}`} checked={form.pricingMode === "fixed"} onChange={() => update("pricingMode", "fixed")} /><span><strong>Precio fijo</strong><small>Usa un importe exacto por unidad.</small></span></label>
          </fieldset>
        </div>
        {form.pricingMode === "percentage" ? <label className="service-pricing-adjustment-field">Ajuste sobre el precio base (%)<input type="number" min="-100" max="1000" step="0.01" value={form.adjustmentPercent} onChange={(event) => update("adjustmentPercent", event.target.value)} /><small className="service-config-help">Usá un número positivo para recargo y uno negativo para descuento. Ej.: 15 = +15%.</small></label> : <label className="service-pricing-adjustment-field">Precio fijo por unidad ({currency ?? "moneda"})<input type="number" min="0" step="0.01" value={form.fixedUnitPrice} onChange={(event) => update("fixedUnitPrice", event.target.value)} placeholder="5800.00" /><small className="service-config-help">Ingresá el importe normal de la moneda, por ejemplo 500,00. El sistema lo guarda internamente como 50.000 centavos y lo convierte una sola vez.</small></label>}
        <fieldset className="service-pricing-schedule">
          <legend>Calendario de aplicación</legend>
          <p className="service-pricing-field-help">Elegí los días y el horario local del negocio. Un horario que cruza medianoche también es válido.</p>
          <div className="service-pricing-days">{weekdayLabels.map((label, day) => <label className={`service-pricing-day ${form.weekdays.includes(day) ? "selected" : ""}`} key={label}><input type="checkbox" checked={form.weekdays.includes(day)} onChange={() => update("weekdays", form.weekdays.includes(day) ? form.weekdays.filter((value) => value !== day) : [...form.weekdays, day])} />{label}</label>)}</div>
          <div className="form-grid"><label>Hora desde<input type="time" value={form.timeFrom} onChange={(event) => update("timeFrom", event.target.value)} /></label><label>Hora hasta<input type="time" value={form.timeUntil} onChange={(event) => update("timeUntil", event.target.value)} /></label></div>
          <div className="form-grid"><label>Fecha desde<input type="date" value={form.dateFrom} onChange={(event) => update("dateFrom", event.target.value)} /></label><label>Fecha hasta<input type="date" value={form.dateUntil} onChange={(event) => update("dateUntil", event.target.value)} /></label></div>
          <label>Zona horaria de la regla<input value={form.timezone} maxLength={80} onChange={(event) => update("timezone", event.target.value)} placeholder="America/Argentina/Buenos_Aires" /><small className="service-config-help">Usá un identificador IANA, por ejemplo America/Argentina/Buenos_Aires.</small></label>
        </fieldset>
        <details className="service-pricing-advanced">
          <summary>Más condiciones y límites opcionales</summary>
          <div className="service-pricing-advanced-body">
            <p className="service-pricing-advanced-intro">Ocupación es el porcentaje de capacidad ya ocupada o retenida para ese horario. Los mínimos y máximos son inclusivos. Anticipación son los minutos entre ahora y el comienzo del turno; dejá un campo vacío para no filtrarlo.</p>
            <div className="form-grid"><label>Ocupación mínima (%)<input type="number" min="0" max="100" step="1" value={form.minOccupancyPercent} onChange={(event) => update("minOccupancyPercent", event.target.value)} /></label><label>Ocupación máxima (%)<input type="number" min="0" max="100" step="1" value={form.maxOccupancyPercent} onChange={(event) => update("maxOccupancyPercent", event.target.value)} /></label></div>
            <div className="form-grid"><label>Anticipación mínima (min)<input type="number" min="0" step="1" value={form.minLeadMinutes} onChange={(event) => update("minLeadMinutes", event.target.value)} /></label><label>Anticipación máxima (min)<input type="number" min="0" step="1" value={form.maxLeadMinutes} onChange={(event) => update("maxLeadMinutes", event.target.value)} /></label></div>
            {form.pricingMode === "percentage" ? <div className="form-grid"><label>Precio mínimo por unidad ({currency ?? "moneda"})<input type="number" min="0" step="0.01" value={form.minUnitPrice} onChange={(event) => update("minUnitPrice", event.target.value)} /><small className="service-config-help">Piso después de aplicar el porcentaje.</small></label><label>Precio máximo por unidad ({currency ?? "moneda"})<input type="number" min="0" step="0.01" value={form.maxUnitPrice} onChange={(event) => update("maxUnitPrice", event.target.value)} /><small className="service-config-help">Tope después de aplicar el porcentaje.</small></label></div> : <p className="service-pricing-fixed-note">Con precio fijo no se usan los límites mínimo y máximo por unidad: el importe indicado arriba es el que se cotiza exactamente.</p>}
            <label>Prioridad<input type="number" min="-100000" max="100000" step="1" value={form.priority} onChange={(event) => update("priority", event.target.value)} /><small className="service-config-help">Si coinciden varias reglas, gana la de mayor prioridad. No se acumulan automáticamente.</small></label>
          </div>
        </details>
        <label className="service-config-toggle service-pricing-active-toggle"><input type="checkbox" checked={form.enabled} onChange={(event) => update("enabled", event.target.checked)} /><span><strong>Activar esta regla al guardar</strong><small>El motor sólo aplicará reglas activas a nuevas cotizaciones.</small></span></label>
        <div className="service-pricing-form-footer"><span className="service-config-help">El precio final siempre se calcula en el servidor.</span><div className="button-row"><button type="button" className="button secondary" disabled={busy} onClick={resetForm}>Limpiar</button>{form.id && <button type="button" className="button secondary" disabled={busy} onClick={resetForm}>Cancelar edición</button>}<button className="button primary" disabled={busy}>{busy ? "Guardando…" : form.id ? "Guardar regla" : "Crear regla"}</button></div></div>
      </form>
    </div>}
  </section>;
}
